import { useTranslation } from "react-i18next";import React from 'react';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger } from
"@/shared/components/ui/dropdown-menu";
import {
  FileText,
  Video,
  Music,
  Image as ImageIcon,
  FileSpreadsheet,
  FileBox,
  MoreVertical,
  Download,
  Eye,
  Share2,
  FileArchive,
  Pencil,
  Trash2 } from
'lucide-react';
import { cn } from '@/shared/utils';
import { toast } from 'sonner';

const FILE_TYPE_CONFIG = {
  pdf: { icon: FileText, color: 'text-red-500', bgColor: 'bg-red-50' },
  word: { icon: FileBox, color: 'text-blue-500', bgColor: 'bg-blue-50' },
  excel: { icon: FileSpreadsheet, color: 'text-green-500', bgColor: 'bg-green-50' },
  powerpoint: { icon: FileArchive, color: 'text-orange-500', bgColor: 'bg-orange-50' },
  mp4: { icon: Video, color: 'text-purple-500', bgColor: 'bg-purple-50' },
  mp3: { icon: Music, color: 'text-indigo-500', bgColor: 'bg-indigo-50' },
  image: { icon: ImageIcon, color: 'text-pink-500', bgColor: 'bg-pink-50' }
};

export const MaterialCard = ({ material, onEdit, onDelete, onPreview }) => {const { t } = useTranslation("crm");
  const { title, description, file_type, thumbnail_url, category, tags = [], owner, updated_at, file_url } = material;
  const config = FILE_TYPE_CONFIG[file_type.toLowerCase()] || { icon: FileText, color: 'text-gray-500', bgColor: 'bg-gray-50' };
  const Icon = config.icon;

  const handleAction = (action) => {
    toast.info(`${action}: ${title}`, {
      description: "Functionality will be available in the next release."
    });
  };

  return (
    <Card className="card-elevated group hover:shadow-lg transition-all duration-300 border-none bg-card">
            <CardContent className="p-0">
                {/* Thumbnail Area */}
                <div className="relative aspect-video w-full overflow-hidden rounded-t-xl bg-muted/30">
                    {thumbnail_url ?
          <img
            src={thumbnail_url}
            alt={title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /> :


          <div className="flex h-full w-full items-center justify-center">
                            <div className={cn("p-4 rounded-full", config.bgColor)}>
                                <Icon className={cn("h-10 w-10", config.color)} />
                            </div>
                        </div>
          }

                    {/* Overlay Actions */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Button
              size="icon"
              variant="secondary"
              className="h-9 w-9 rounded-full shadow-lg"
              onClick={() => onPreview ? onPreview(material) : handleAction('Previewing')}>
              
                            <Eye className="h-4 w-4" />
                        </Button>
                        <Button
              size="icon"
              variant="secondary"
              className="h-9 w-9 rounded-full shadow-lg"
              onClick={() => handleAction('Downloading')}>
              
                            <Download className="h-4 w-4" />
                        </Button>
                        <Button
              size="icon"
              variant="secondary"
              className="h-9 w-9 rounded-full shadow-lg"
              onClick={() => handleAction('Sharing')}>
              
                            <Share2 className="h-4 w-4" />
                        </Button>
                    </div>

                    <Badge className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm text-foreground hover:bg-white/100 border-none shadow-sm">
                        {category}
                    </Badge>

                    <div className="absolute top-3 right-3">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                  variant="secondary"
                  size="icon"
                  className="h-8 w-8 rounded-full bg-white/90 backdrop-blur-sm text-foreground hover:bg-white border-none shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">
                  
                                    <MoreVertical className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-40 rounded-xl border-none shadow-xl p-1">
                                <DropdownMenuItem
                  onClick={() => onEdit(material)}
                  className="flex items-center gap-2 px-3 py-2 cursor-pointer rounded-lg hover:bg-teal-50 text-teal-700 font-medium">
                  
                                    <Pencil className="h-4 w-4" />{t("edit", "Edit")}

                </DropdownMenuItem>
                                <DropdownMenuItem
                  onClick={() => onDelete(material)}
                  className="flex items-center gap-2 px-3 py-2 cursor-pointer rounded-lg hover:bg-red-50 text-red-600 font-medium">
                  
                                    <Trash2 className="h-4 w-4" />{t("delete", "Delete")}

                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>

                {/* Content Area */}
                <div className="p-4 space-y-3">
                    <div className="flex items-start justify-between">
                        <div>
                            <h3 className="font-bold text-lg leading-tight line-clamp-1 font-['Space_Grotesk'] text-foreground group-hover:text-teal-600 transition-colors">
                                {title}
                            </h3>
                            <p className="text-sm text-muted-foreground line-clamp-2 mt-1 min-h-[40px]">
                                {description}
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-1">
                        {tags.slice(0, 2).map((tag) =>
            <Badge key={tag} variant="outline" className="text-[10px] font-medium px-2 py-0">
                                {tag}
                            </Badge>
            )}
                        {tags.length > 2 &&
            <span className="text-[10px] text-muted-foreground">+{tags.length - 2}{t("more", "more")}</span>
            }
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-border/50 text-[11px] text-muted-foreground">
                        <div className="flex items-center gap-1.5">
                            <div className="h-5 w-5 rounded-full bg-teal-500/10 flex items-center justify-center text-teal-600 font-bold uppercase">
                                {owner?.charAt(0) || 'A'}
                            </div>
                            <span>{owner || 'Admin'}</span>
                        </div>
                        <span>{updated_at}</span>
                    </div>
                </div>
            </CardContent>
        </Card>);

};